import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

type State = 'idle' | 'running' | 'complete' | 'failed' | 'stale';

type StatusResponse = {
  ok: true;
  state: State;
  running_for_seconds: number | null;
  started_at: string | null;
  last_run: (Record<string, unknown> & { at: string }) | null;
  last_error: (Record<string, unknown> & { at: string }) | null;
};

export default function FreewayFinancePanel({ onChanged }: { onChanged: () => void }) {
  const { toast } = useToast();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const prevState = useRef<State | null>(null);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('seed-freeway-finance', {
      body: { action: 'status' },
    });
    if (error) return;
    setStatus(data as StatusResponse);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Poll while a run is in flight.
  useEffect(() => {
    if (status?.state !== 'running') return;
    const t = setInterval(() => { void refresh(); }, 5000);
    return () => clearInterval(t);
  }, [status?.state, refresh]);

  // Notify once when a run finishes.
  useEffect(() => {
    const state = status?.state ?? null;
    if (prevState.current === 'running' && state && state !== 'running') {
      if (state === 'complete') {
        toast({ title: 'Freeway Finance seeded', description: 'Row counts are shown below.' });
        onChanged();
      } else if (state === 'failed') {
        toast({
          title: 'Freeway Finance seeding failed',
          description: String(status?.last_error?.error ?? 'Unknown error'),
          variant: 'destructive',
        });
      }
    }
    prevState.current = state;
  }, [status, toast, onChanged]);

  async function start() {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke('seed-freeway-finance', {
      body: { action: 'start' },
    });
    setBusy(false);
    if (error) {
      toast({ title: 'Could not start seeding', description: error.message, variant: 'destructive' });
      return;
    }
    const res = data as { ok?: boolean; error?: string };
    if (res?.ok !== true) {
      toast({ title: 'Could not start seeding', description: res?.error ?? 'Unknown error', variant: 'destructive' });
      void refresh();
      return;
    }
    toast({ title: 'Seeding started', description: 'This takes about two minutes.' });
    void refresh();
  }

  const running = status?.state === 'running';
  const rows = status?.last_run?.rows as Record<string, number> | undefined;

  return (
    <div className="border border-border rounded-lg bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-lg font-semibold">Freeway Finance</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Large financial-services demo tenant: 5,100 employees, 12 months of daily history, all seven
        anchors, and the positive → sales-decline → restructuring arc. The seeder is idempotent and
        wipes and rebuilds only this organization. Other tenants are untouched.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={start} disabled={busy || running}>
          {(busy || running) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {running ? `Seeding… ${status?.running_for_seconds ?? 0}s` : 'Seed Freeway Finance'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => void refresh()} disabled={busy}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh status
        </Button>
        {status?.state === 'stale' && (
          <span className="text-xs text-muted-foreground">
            Last run never reported back. Safe to run again.
          </span>
        )}
      </div>

      {status?.last_error && (
        <p className="text-xs text-destructive">
          Last run failed: {String(status.last_error.error)}
        </p>
      )}

      {status?.last_run && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Last completed {new Date(status.last_run.at).toLocaleString()} in{' '}
            {String(status.last_run.elapsed_seconds ?? '?')}s.
          </p>
          {rows && (
            <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3 lg:grid-cols-4">
              {Object.entries(rows).map(([table, count]) => (
                <div key={table} className="flex justify-between gap-2 border-b border-border/50 py-0.5">
                  <span className="text-muted-foreground truncate">{table}</span>
                  <span className="tabular-nums font-medium">{count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
